import React from "react";
import { startGoogleLogin } from "../services/authService";

interface Props {
  currentUserEmail?: string;
}

const docBg =
  "https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=1600&q=60";
// Visuels locaux
const cerfaThumb = "/images/rédaction cerfa.png";
const idThumb = "/images/carteidentite.png";
const bureauThumb = "/images/garage.png";

const LandingPage: React.FC<Props> = ({ currentUserEmail }) => {
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Hero */}
      <div
        className="relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(120deg, rgba(15,23,42,0.9), rgba(15,23,42,0.6)), url(${docBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <div className="max-w-3xl space-y-6">
            <p className="text-sm uppercase tracking-[0.25em] text-sky-300">Garages partenaires</p>
            <h1 className="text-4xl lg:text-5xl font-bold leading-tight">
              Scannez, remplissez, annotez vos Cerfa en quelques secondes
            </h1>
            <p className="text-lg text-slate-200">
              AutoCerfa centralise l’OCR carte grise / identité et le pré-remplissage du Cerfa pour vos dossiers
              d’immatriculation. Nous gérons l’amélioration continue de l’OCR en arrière-plan.
            </p>
            <div className="flex flex-wrap gap-3 items-center">
              <button
                onClick={startGoogleLogin}
                className="px-5 py-3 rounded-lg bg-white text-slate-900 font-semibold hover:bg-slate-100 transition"
              >
                Continuer avec Google
              </button>
              {currentUserEmail && (
                <span className="text-sm text-slate-200">
                  Connecté en tant que <span className="font-semibold text-white">{currentUserEmail}</span>
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-200">
              <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-500/40">
                Bureau en ligne : dossiers de vente, factures PDF, reprise à tout moment
              </span>
              <span className="px-3 py-1 rounded-full bg-sky-500/20 text-sky-200 border border-sky-500/40">
                19,90 €/mois : toutes les fonctionnalités incluses
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Grille de visuels */}
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden shadow-lg">
            <img src={cerfaThumb} alt="Carte grise" className="w-full h-40 object-cover opacity-90" />
            <div className="p-4 space-y-2">
              <h3 className="text-lg font-semibold">Carte grise & Cerfa</h3>
              <p className="text-sm text-slate-300">
                Scan carte grise + pré-remplissage Cerfa : immatriculation, VIN, J.1, D.1/D.2…
              </p>
            </div>
          </div>
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden shadow-lg">
            <img src={idThumb} alt="Identité / Résidence" className="w-full h-40 object-cover opacity-90" />
            <div className="p-4 space-y-2">
              <h3 className="text-lg font-semibold">Identité / Résidence</h3>
              <p className="text-sm text-slate-300">
                Capture identité / résident / passeport / justificatif pour compléter les dossiers.
              </p>
            </div>
          </div>
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden shadow-lg">
            <img src={bureauThumb} alt="Bureau en ligne" className="w-full h-40 object-cover opacity-90" />
            <div className="p-4 space-y-2">
              <h3 className="text-lg font-semibold">Dossiers, annotation & facture</h3>
              <p className="text-sm text-slate-300">
                Dossiers centralisés, annotation des blocs, génération de facture et export ZIP des pièces.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tutoriel défilant */}
      <div className="bg-slate-900/80 border-t border-slate-800">
        <div className="max-w-5xl mx-auto px-6 py-12 space-y-6">
          <h2 className="text-2xl font-semibold">Guide rapide pour les garages</h2>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-slate-200">
            <div className="p-4 rounded-lg bg-slate-800/80 border border-slate-700">
              <p className="text-sky-300 text-xs font-semibold mb-2">Étape 1</p>
              <h4 className="font-semibold mb-1">Connexion Google</h4>
              <p>Connectez-vous avec le compte partenaire pour accéder au scan et au Cerfa.</p>
            </div>
            <div className="p-4 rounded-lg bg-slate-800/80 border border-slate-700">
              <p className="text-sky-300 text-xs font-semibold mb-2">Étape 2</p>
              <h4 className="font-semibold mb-1">Scan & préremplissage</h4>
              <p>Uploadez la carte grise / identité, le Cerfa se remplit automatiquement.</p>
            </div>
            <div className="p-4 rounded-lg bg-slate-800/80 border border-slate-700">
              <p className="text-sky-300 text-xs font-semibold mb-2">Étape 3</p>
              <h4 className="font-semibold mb-1">Dossier & suivi</h4>
              <p>Regroupez les documents, reprenez un dossier en cours, et préparez les prochaines features (export, partage) à venir.</p>
            </div>
          </div>
          <div className="text-sm text-slate-300 space-y-1">
            <div>Inclut : scan CG/ID/justif, Cerfa, dossiers, annotation, facture PDF, archivage ZIP, suivi de stockage.</div>
            <div>Besoin d’aide ? Support pour la mise en place dans votre garage. Toutes les fonctionnalités pour 19,90 €/mois.</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
